import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";

// ─── KpiCard ──────────────────────────────────────────────────────────────────
//  Tile de indicador reutilizável: label + valor em destaque + subtítulo opcional.
//  C: tema (card, border, text, muted) — mesma forma usada nas páginas do módulo.

export function KpiCard({ label, value, sub, color, C }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
      }}
    >
      <span style={{
        fontSize: 11, fontWeight: 700, color: C.muted,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 22, fontWeight: 800, color: color || C.text,
        lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {value}
      </span>
      {sub && (
        <span style={{ fontSize: 11, color: C.muted }}>{sub}</span>
      )}
    </div>
  );
}

export function KpiRow({ items, C }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        maxWidth: "100%",
      }}
    >
      {items.map((item) => (
        <KpiCard key={item.label} C={C} {...item} />
      ))}
    </div>
  );
}

// ─── KpiCardOrcamento / KpiRowOrcamento ────────────────────────────────────────
// Variante usada só nas telas de Gestão Orçamentária (Despesas Gerais / Custos Gerais por
// Projeto): mesma moldura do KpiCard genérico acima (intocado, outras dashboards continuam
// usando-o), mas com um acessório visual à direita do valor — sparkline mensal, anel de
// progresso ou seta de variação — para reproduzir o layout de referência da tela de OPEX.
export function KpiCardOrcamento({ label, value, sub, color, accessory, C }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: "16px 18px", display: "flex", flexDirection: "column", gap: 8, minWidth: 0,
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{
          fontSize: 22, fontWeight: 800, color: color || C.text, lineHeight: 1.15,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {value}
        </span>
        {accessory}
      </div>
      {sub && <span style={{ fontSize: 11, color: C.muted }}>{sub}</span>}
    </div>
  );
}

export function KpiRowOrcamento({ items, C }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, maxWidth: "100%" }}>
      {items.map((item) => (
        <KpiCardOrcamento key={item.label} C={C} {...item} />
      ))}
    </div>
  );
}

// Mini-gráfico de linha, decorativo, ao lado do valor já rotulado no card — mostra só a
// tendência dos 12 meses, sem eixo/grid (convenção de sparkline). O detalhe mês a mês fica
// disponível via <title> nativo no hover, como camada mínima de interação.
export function Sparkline({ data, color, width = 76, height = 28 }) {
  const valores = (data || []).map((v) => Number(v) || 0);
  if (valores.length < 2) return null;
  const min = Math.min(...valores, 0);
  const max = Math.max(...valores, 0);
  const amplitude = max - min || 1;
  const passoX = width / (valores.length - 1);
  const pontos = valores.map((v, i) => [i * passoX, height - ((v - min) / amplitude) * height]);
  const path = pontos.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [ultimoX, ultimoY] = pontos[pontos.length - 1];
  const titulo = valores.map((v, i) => `${MESES_ABREV[i]}: ${formatCurrency(v)}`).join(" · ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block", overflow: "visible", flexShrink: 0 }}>
      <title>{titulo}</title>
      <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={ultimoX} cy={ultimoY} r={2.5} fill={color} />
    </svg>
  );
}

// Anel de progresso (0–100%) — usado no card "Progresso Orçamentário": % de categorias mãe
// já marcadas como concluídas pelo próprio usuário (ver checkbox nos botões-KPI de categoria).
export function GaugeProgresso({ percent, color, trackColor, size = 52, thickness = 6 }) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const raio = (size - thickness) / 2;
  const circunferencia = 2 * Math.PI * raio;
  const preenchido = (pct / 100) * circunferencia;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", flexShrink: 0 }}>
      <title>{`${Math.round(pct)}% das categorias mãe concluídas`}</title>
      <circle cx={size / 2} cy={size / 2} r={raio} fill="none" stroke={trackColor} strokeWidth={thickness} />
      <circle
        cx={size / 2} cy={size / 2} r={raio} fill="none" stroke={color} strokeWidth={thickness}
        strokeDasharray={`${preenchido} ${circunferencia - preenchido}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.3} fontWeight={800} fill={color}>
        {Math.round(pct)}%
      </text>
    </svg>
  );
}

// Seta de variação (para o card "Variação Anual") — cor por status (positivo/negativo/neutro),
// nunca só decorativa: sempre acompanhada do valor formatado em texto ao lado, no card.
export function TrendAccessory({ direcao, C }) {
  const cor = direcao === "up" ? C.success : direcao === "down" ? C.danger : C.muted;
  const Icone = direcao === "up" ? TrendingUp : direcao === "down" ? TrendingDown : Minus;
  return (
    <span style={{
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      width: 32, height: 32, borderRadius: "50%", background: `${cor}1F`, color: cor,
    }}>
      <Icone size={16} />
    </span>
  );
}
